import * as React from 'react';
import ListItemText from '@mui/material/ListItemText';
import { CmicContext } from '../../../context/CmicContext';
import { ActionContext } from '../../../context/ActionContext';
import { MessageBarType } from '@fluentui/react';
import { MdWarning } from 'react-icons/md';
import { isBGClrDark, splitLastOccurrence } from '../../../utility/UtilityFunctions';
import { hasUnsupportedFileNameChars, UNSUPPORTED_FILE_NAME_MSG } from '../../../shared/UtilityFunctions';
import { baseFontFamilyStyle, unsupportedFileNameIconStyle } from '../../../shared/styles';
import { COLORS } from '../../../utility/tasks/colors';

function InputBox({ value, requiredData, labelId, handleCallBackForInputext }) {
	const { currentOfficeTheme, setShowToast } = React.useContext(CmicContext);
	const { selectedAttachmentsIds } = React.useContext(ActionContext);
	const [showInputElement, setShowInputElement] = React.useState(false);
	const [inputValue, setInputValue] = React.useState('');
	const [fileExtension, setFileExtension] = React.useState('');
	const [lastClickTime, setLastClickTime] = React.useState(0);
	const doubleClickDelay = 600;
	const pressTimer = React.useRef(null);

	React.useEffect(() => {
		setInputValue(value);
	}, [value]);

	const truncateString = (str, len) => {
		return str.length > 20 ? str.substring(0, 20) + '...' : str;
	};

	const handleClick = () => {
		const currentTime = new Date().getTime();
		const timeSinceLastClick = currentTime - lastClickTime;

		if (timeSinceLastClick < doubleClickDelay) {
			// Double click
			handleDoubleClick(event);
			setLastClickTime(0);
		} else {
			setLastClickTime(currentTime);
		}
	};

	const handleMouseDown = () => {
		pressTimer.current = setTimeout(() => {
			handleDoubleClick();
		}, 600);
	};

	const handleMouseUp = () => {
		clearTimeout(pressTimer.current);
	};

	const handleDoubleClick = () => {
		if (!(selectedAttachmentsIds[requiredData] === requiredData)) {
			let { attachmentName, extension } = splitLastOccurrence(inputValue);
			setFileExtension(extension);
			setInputValue(attachmentName);
			setShowInputElement(true);
		} else {
			setShowToast({
				isVisible: true,
				msg: `Attachment non-editable`,
				type: MessageBarType.warning,
			});
		}
	};

	const handleChange = event => {
		setInputValue(event.target.value);
	};

	const handleFreezeText = () => {
		// Keep the field open until the name is valid, so it never reaches the upload
		if (hasUnsupportedFileNameChars(inputValue.concat(fileExtension))) {
			setShowToast({
				isVisible: true,
				msg: UNSUPPORTED_FILE_NAME_MSG,
				type: MessageBarType.warning,
			});
			return;
		}

		if (inputValue.trim()) {
			let conCatenatedInput = inputValue.concat(fileExtension);
			setInputValue(conCatenatedInput);
			handleCallBackForInputext(conCatenatedInput);
			setShowInputElement(false);
		} else {
			let conCatenatedInput = value;
			setInputValue(conCatenatedInput);
			handleCallBackForInputext(conCatenatedInput);
			setShowInputElement(false);
		}
	};

	const isNameUnsupported = hasUnsupportedFileNameChars(inputValue);

	const handleBlur = () => {
		handleFreezeText();
	};

	const handleKeyPress = event => {
		if (event.charCode === 13) {
			handleFreezeText();
		}
	};

	return (
		<>
			{showInputElement ? (
				<input
					type="text"
					value={inputValue}
					onChange={e => handleChange(e)}
					onBlur={handleBlur}
					maxLength={50}
					onKeyPress={e => {
						handleKeyPress(e);
					}}
					autoFocus
				/>
			) : (
				<ListItemText
					sx={{
						margin: '0px',
						'& .MuiTypography-root': {
							padding: '0px',
							fontFamily: baseFontFamilyStyle,
							color: isNameUnsupported
								? isBGClrDark()
									? COLORS.RED_ON_DARK
									: COLORS.RED
								: currentOfficeTheme.bodyForegroundColor,
							width: '100%',
							height: '100%',
							fontSize: '13px',
						},
					}}
					onClick={handleClick}
					onMouseDown={handleMouseDown}
					onMouseUp={handleMouseUp}
					onTouchStart={handleMouseDown}
					onTouchEnd={handleMouseUp}
					id={labelId}
					primary={
						<>
							{isNameUnsupported && <MdWarning style={unsupportedFileNameIconStyle} />}
							{`${truncateString(inputValue, 25)} `}
						</>
					}
				/>
			)}
		</>
	);
}

export default InputBox;
